import type {
  PublicCompanyChemicalResponse,
  PublicLiveAttendanceJoinRequest,
  PublicLiveAttendanceJoinResponse,
  PublicLiveAttendancePinLookupResponse,
  PublicLiveAttendanceSendPinResponse,
  PublicSafetyMeetingLiveAttendance,
  SafetyPassportPayload,
  SafetyProfilePayload,
} from "@miter/types/contracts";

export class PassportNotFoundError extends Error {
  constructor() {
    super("Passport not found");
    this.name = "PassportNotFoundError";
  }
}

export class SafetyProfileNotFoundError extends Error {
  constructor() {
    super("Safety profile not found");
    this.name = "SafetyProfileNotFoundError";
  }
}

export class SdsNotFoundError extends Error {
  constructor() {
    super("Safety data sheet not found");
    this.name = "SdsNotFoundError";
  }
}

/** The code is unknown, or its session is closed — indistinguishable by design. */
export class AttendanceNotFoundError extends Error {
  constructor() {
    super("Attendance sign-in not found");
    this.name = "AttendanceNotFoundError";
  }
}

/** The session closed or its window lapsed between loading the page and this request. */
export class AttendanceUnavailableError extends Error {
  constructor() {
    super("Attendance sign-in is no longer available");
    this.name = "AttendanceUnavailableError";
  }
}

export class PinNotRecognizedError extends Error {
  constructor() {
    super("PIN not recognized");
    this.name = "PinNotRecognizedError";
  }
}

export class AlreadyOnListError extends Error {
  constructor() {
    super("Already signed in");
    this.name = "AlreadyOnListError";
  }
}

export class TooManyAttemptsError extends Error {
  constructor() {
    super("Too many attempts");
    this.name = "TooManyAttemptsError";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

class APIHandler {
  static async request(path: string, method: Method, body?: unknown): Promise<Response> {
    return await fetch(`${import.meta.env.REACT_APP_MITER_BACKEND_API}${path}`, {
      method,
      // Unauthenticated: the token in the URL is the credential, so no auth header (mirrors careers).
      headers: {
        "Content-Type": "application/json",
        "client-service": "safety",
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  }
}

const liveAttendancePath = (code: string): string => `/public-safety-meeting-live-attendance/${code}`;

export const MiterAPI = {
  safety: {
    sds: {
      retrieve: async (token: string): Promise<PublicCompanyChemicalResponse> => {
        const response = await APIHandler.request(`/public-sds/${token}`, "GET");

        if (response.status === 404) throw new SdsNotFoundError();
        if (!response.ok) throw new Error("Unable to load this safety data sheet.");

        return await response.json();
      },
    },
    profiles: {
      retrieve: async (slug: string): Promise<SafetyProfilePayload> => {
        const response = await APIHandler.request(`/public-safety-profile/${slug}`, "GET");

        if (response.status === 404) throw new SafetyProfileNotFoundError();
        if (!response.ok) throw new Error("Unable to load this safety profile.");

        return await response.json();
      },
    },
    passports: {
      retrieve: async (token: string): Promise<SafetyPassportPayload> => {
        const response = await APIHandler.request(`/safety-passport/${token}`, "GET");

        // Unknown token or a company that hasn't enabled the passport both come back as
        // 404. Surface a dedicated error so the page can render a "not found" state.
        if (response.status === 404) throw new PassportNotFoundError();

        if (!response.ok) throw new Error("Unable to load this safety passport.");

        return await response.json();
      },
    },
    liveAttendance: {
      retrieve: async (code: string): Promise<PublicSafetyMeetingLiveAttendance> => {
        const response = await APIHandler.request(liveAttendancePath(code), "GET");

        if (response.status === 404) throw new AttendanceNotFoundError();
        if (!response.ok) throw new Error("Unable to load this meeting sign-in.");

        return await response.json();
      },
      lookupPin: async (code: string, pin: string): Promise<PublicLiveAttendancePinLookupResponse> => {
        const response = await APIHandler.request(`${liveAttendancePath(code)}/pin-lookup`, "POST", {
          pin,
        });

        if (response.status === 429) throw new TooManyAttemptsError();
        // Both a wrong PIN and a session that closed since page load come back as 404; only the
        // body distinguishes them, so an unavailable session refetches instead of blaming the PIN.
        if (response.status === 404) {
          const body = (await response.json().catch(() => null)) as { error?: string } | null;
          if (body?.error?.includes("sign-in link")) throw new AttendanceUnavailableError();
          throw new PinNotRecognizedError();
        }
        if (!response.ok) throw new Error("Unable to check that PIN.");

        return await response.json();
      },
      sendPin: async (code: string, phoneNumber: string): Promise<PublicLiveAttendanceSendPinResponse> => {
        const response = await APIHandler.request(`${liveAttendancePath(code)}/send-pin`, "POST", {
          phone_number: phoneNumber,
        });

        if (response.status === 429) throw new TooManyAttemptsError();
        if (response.status === 404) throw new AttendanceUnavailableError();
        if (!response.ok) throw new Error("Unable to text your PIN.");

        return await response.json();
      },
      join: async (
        code: string,
        params: PublicLiveAttendanceJoinRequest
      ): Promise<PublicLiveAttendanceJoinResponse> => {
        const response = await APIHandler.request(`${liveAttendancePath(code)}/join`, "POST", params);

        if (response.status === 409) throw new AlreadyOnListError();
        if (response.status === 404) throw new AttendanceUnavailableError();
        if (!response.ok) throw new Error("Unable to submit your attendance.");

        return await response.json();
      },
    },
  },
};
